import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Plane,
  MapPin,
  Calendar,
  User,
  Building,
  CheckCircle,
  Clock,
  Loader2,
  Filter,
  Eye,
  Edit2,
  Plus
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  preparing: { label: 'En préparation', color: 'bg-gray-100 text-gray-700', icon: Clock },
  visa_approved: { label: 'Visa approuvé', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  booked: { label: 'Vol réservé', color: 'bg-purple-100 text-purple-700', icon: Plane },
  in_transit: { label: 'En transit', color: 'bg-yellow-100 text-yellow-700', icon: Plane },
  arrived: { label: 'Arrivé', color: 'bg-green-100 text-green-700', icon: MapPin },
  settled: { label: 'Installé', color: 'bg-emerald-100 text-emerald-700', icon: Building }
};

const ArrivalTrackingPage = () => {
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const [arrivals, setArrivals] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchArrivals();
  }, [statusFilter]);

  const fetchArrivals = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API_URL}/arrivals${params}`);
      setArrivals(res.data.arrivals);
      setStatusCounts(res.data.status_counts);
    } catch (error) {
      console.error('Error fetching arrivals:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (arrivalId, newStatus, notes = null) => {
    try {
      setUpdating(true);
      await axios.patch(`${API_URL}/arrivals/${arrivalId}/status`, {
        status: newStatus,
        notes
      });
      await fetchArrivals();
      setDialogOpen(false);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const getNextStatus = (currentStatus) => {
    const flow = ['preparing', 'visa_approved', 'booked', 'in_transit', 'arrived', 'settled'];
    const currentIndex = flow.indexOf(currentStatus);
    return currentIndex < flow.length - 1 ? flow[currentIndex + 1] : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="arrival-tracking-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            Suivi des arrivées en Allemagne
          </h1>
          <p className="text-gray-500">Suivez le parcours de vos étudiants jusqu'à leur installation</p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const Icon = config.icon;
          const count = statusCounts[status] || 0;
          return (
            <Card 
              key={status} 
              className={`border-0 shadow-sm cursor-pointer transition-all ${statusFilter === status ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5 text-gray-400" />
                  <span className="text-2xl font-bold">{count}</span>
                </div>
                <p className="text-xs text-gray-500">{config.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all_statuses', lang)}</SelectItem>
            {Object.entries(statusConfig).map(([status, config]) => (
              <SelectItem key={status} value={status}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Arrivals List */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {arrivals.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Plane className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun suivi d'arrivée trouvé</p>
            </div>
          ) : (
            <div className="divide-y">
              {arrivals.map(arrival => {
                const config = statusConfig[arrival.status];
                const Icon = config?.icon || Clock;
                return (
                  <div
                    key={arrival.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedArrival(arrival);
                      setDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config?.color || 'bg-gray-100'}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{arrival.student_name}</h3>
                          <Badge className={config?.color}>
                            {config?.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {arrival.arrival_city}
                          </span>
                          {arrival.planned_arrival_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(arrival.planned_arrival_date), 'dd MMM yyyy', { locale: fr })}
                            </span>
                          )}
                          {arrival.partner_name && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {arrival.partner_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails du suivi d'arrivée</DialogTitle>
          </DialogHeader>
          
          {selectedArrival && (
            <div className="space-y-4">
              {/* Status Timeline */}
              <div className="flex items-center justify-between py-4">
                {Object.entries(statusConfig).map(([status, config], index) => {
                  const Icon = config.icon;
                  const isActive = Object.keys(statusConfig).indexOf(selectedArrival.status) >= index;
                  const isCurrent = selectedArrival.status === status;
                  
                  return (
                    <div key={status} className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCurrent ? 'bg-primary text-white' : isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className={`text-xs mt-1 ${isCurrent ? 'font-bold text-primary' : 'text-gray-400'}`}>
                        {config.label.split(' ')[0]}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Student Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{selectedArrival.student_name}</span>
                </div>
                {selectedArrival.student_email && (
                  <p className="text-sm text-gray-500 ml-6">{selectedArrival.student_email}</p>
                )}
                {selectedArrival.student_phone && (
                  <p className="text-sm text-gray-500 ml-6">{selectedArrival.student_phone}</p>
                )}
              </div>

              {/* Arrival Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-500">lang === 'de' ? 'Ankunftsstadt' : "Ville d'arrivée"</Label>
                  <p className="font-medium">{selectedArrival.arrival_city}</p>
                </div>
                <div>
                  <Label className="text-gray-500">{lang === 'de' ? 'Geplantes Datum' : 'Date prévue'}</Label>
                  <p className="font-medium">
                    {selectedArrival.planned_arrival_date 
                      ? format(new Date(selectedArrival.planned_arrival_date), 'dd MMMM yyyy', { locale: fr })
                      : 'Non définie'}
                  </p>
                </div>
                {selectedArrival.flight_number && (
                  <div>
                    <Label className="text-gray-500">{lang === 'de' ? 'Flug' : 'Vol'}</Label>
                    <p className="font-medium">{selectedArrival.flight_number}</p>
                  </div>
                )}
                {selectedArrival.accommodation_type && (
                  <div>
                    <Label className="text-gray-500">{lang === 'de' ? 'Unterkunft' : 'Hébergement'}</Label>
                    <p className="font-medium">{selectedArrival.accommodation_type}</p>
                  </div>
                )}
              </div>

              {selectedArrival.notes && (
                <div>
                  <Label className="text-gray-500">{t('course_detail_grades', lang)}</Label>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg mt-1">{selectedArrival.notes}</p>
                </div>
              )}

              {/* Actions */}
              <DialogFooter className="gap-2">
                {getNextStatus(selectedArrival.status) && (
                  <Button
                    onClick={() => updateStatus(selectedArrival.id, getNextStatus(selectedArrival.status))}
                    disabled={updating}
                    className="flex-1"
                  >
                    {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Passer à "{statusConfig[getNextStatus(selectedArrival.status)]?.label}"
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArrivalTrackingPage;
